import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { makeAutoObservable, reaction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { DomainProductLineItem, DomainProductLinesDelegate } from '../domainProductLinesDelegate';

interface DomainParams {
  rateSegment: RateSegment;
  disableBCFields?: boolean;
}

type AlliedProposalBaseFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  sic4: FormControl<string>;
};

/** Blocklist of ineligible SIC codes/prefixes */
const ALLIED_ELIGIBLE_SIC_CODE_REGEX =
  /^(?!076[1-9]|07[7-8][0-3]|723[1-9]|7240|7241|736[1-3]|7389|8021|8071|8072|86\d{2}|8811|8999|9721)\d{4}$/;

export class AlliedProposalFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public proposalForm: FormController<AlliedProposalBaseFields>;

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this.domainProductLinesDelegate.domainProductLineItems;
  }

  public get isAvailableProductLinesLoading(): boolean {
    return this.domainProductLinesDelegate.isLoading;
  }

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  private get _canFetchProductLines(): boolean {
    const {
      fields: { effectiveDate, zip, state, county, sic4 },
    } = this.proposalForm;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg &&
      !sic4.unpassedValidationMsg
    );
  }

  constructor(params: DomainParams) {
    const disableBCFields = params.disableBCFields || false;

    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>('', { disabled: disableBCFields }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false, disabled: disableBCFields },
              state: {
                disabled: disableBCFields,
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
              county: { disabled: disableBCFields },
            },
          },
          {
            type: 'sic',
            params: {
              disabled: disableBCFields,
              additionalValidations: [
                {
                  check: (value: string): boolean => ALLIED_ELIGIBLE_SIC_CODE_REGEX.test(value),
                  msg: 'Ineligible SIC Code',
                },
              ],
            },
          },
        ],
      },
    );

    this.domainProductLinesDelegate = new DomainProductLinesDelegate({
      rateSegment: params.rateSegment,
    });

    reaction(
      () => [
        this.proposalForm.fields.effectiveDate.value,
        this.proposalForm.fields.zip.value,
        this.proposalForm.fields.state.value,
        this.proposalForm.fields.county.value,
        this.proposalForm.fields.sic4.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.proposalForm.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
